import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  MenuItem,
  ListItemButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LocalizedLink from "./LocalizedLink";
import PelgroupLogo from "./PelgroupLogo";
import LanguageSelector from "./LanguageSelector";
import "../assets/Navbar.css";

function Navbar({ routes }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openMenuName, setOpenMenuName] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [menuTimeout, setMenuTimeout] = React.useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const isMobile = useMediaQuery("(max-width:950px)");
  const isSmallScreen = useMediaQuery("(max-width:740px)");

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleMenuOpen = (event, name) => {
    clearTimeout(menuTimeout);
    setAnchorEl(event.currentTarget);
    setOpenMenuName(name);
  };

  const handleMenuClose = () => {
    setMenuTimeout(
      setTimeout(() => {
        setAnchorEl(null);
        setOpenMenuName(null);
      }, 200),
    );
  };

  const toggleDrawer = (open) => {
    setDrawerOpen(open);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        borderRadius: "20px",
        width: "90%",
        maxWidth: "1500px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "rgba(255, 255, 255, 0.78)",
        boxShadow: isScrolled
          ? "0px 4px 10px rgba(0, 0, 0, 0.1)"
          : "0px 8px 15px rgba(0, 0, 0, 0.1)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(196, 106, 45, 0.16)",
        color: "#20242A",
        top: "15px",
        zIndex: 999,
      }}
    >
      <Toolbar
        sx={{
          minHeight: isScrolled ? "65px" : "70px",
        }}
        className="header-toolbar"
      >
        {/* Logo */}
        <Box
          component={LocalizedLink}
          to="/"
          sx={{
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          <PelgroupLogo isScrolled={isSmallScreen ? false : isScrolled} />
        </Box>

        {/* Rest of the Navbar Content */}
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
              mx: 3,
            }}
          >
            {routes.map((option) => (
              <Box
                key={option.name}
                sx={{
                  position: "relative",
                  mx: 1.5,
                  padding: "10px 0",
                }}
                onMouseEnter={(event) => handleMenuOpen(event, option.name)}
                onMouseLeave={handleMenuClose}
              >
                <Typography
                  component={LocalizedLink}
                  to={option.route}
                  sx={{
                    cursor: "pointer",
                    color: "#20242A",
                    fontFamily: "Roboto Regular, sans-serif",
                    fontSize: "14px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {option.name}
                </Typography>
                {/* Using Popper for Dropdown */}
                {option.collapse && (
                  <Popper
                    open={openMenuName === option.name}
                    anchorEl={anchorEl}
                    onMouseEnter={() => clearTimeout(menuTimeout)}
                    onMouseLeave={handleMenuClose}
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={[
                      {
                        name: "offset",
                        options: {
                          offset: [0, 15],
                        },
                      },
                    ]}
                    sx={{
                      zIndex: 1200,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: "-10px",
                        left: "15px",
                        borderLeft: "10px solid transparent",
                        borderRight: "10px solid transparent",
                        borderBottom: "10px solid rgba(255, 255, 255, 0.96)",
                      },
                    }}
                  >
                    <ClickAwayListener onClickAway={handleMenuClose}>
                      <Paper
                        elevation={3}
                        sx={{
                          padding: "15px",
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.96)",
                          backdropFilter: "blur(4px)",
                          boxShadow: "0px 8px 15px rgba(0, 0, 0, 0.1)",
                          position: "relative",
                        }}
                      >
                        {option.collapse.map((suboption) => (
                          <MenuItem
                            key={suboption.name}
                            component={LocalizedLink}
                            to={suboption.route}
                            onClick={handleMenuClose}
                            sx={{
                              color: "#20242A",
                              fontFamily: "Roboto Regular, sans-serif",
                              fontSize: "14px",
                              textTransform: "none",
                              mb: 1,
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "center",
                              height: "40px",
                            }}
                          >
                            {suboption.name}
                          </MenuItem>
                        ))}
                      </Paper>
                    </ClickAwayListener>
                  </Popper>
                )}
              </Box>
            ))}
            <Box sx={{ ml: 2 }}>
              <LanguageSelector />
            </Box>
          </Box>
        )}
        {/* Mobile menu icon */}
        {isMobile && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <LanguageSelector sx={{ mr: 2 }} />
            <IconButton
              edge="end"
              onClick={() => toggleDrawer(true)}
              sx={{
                color: "#20242A",
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => toggleDrawer(false)}
          sx={{
            "& .MuiDrawer-paper": {
              width: "180px",
              padding: "20px",
              backgroundColor: "rgba(255, 255, 255, 0.98)",
              color: "#20242A",
              backdropFilter: "blur(12px)",
            },
          }}
        >
          <List>
            {routes.map((option, index) => (
              <React.Fragment key={option.name}>
                {
                  //if current is collapsable and previous is not, add a divider on top of curent to make it
                  option.collapse && !routes[index - 1].collapse && (
                    <Divider sx={{ my: 1 }} />
                  )
                }
                {option.collapse ? (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Roboto Bold, sans-serif",
                            padding: "8px 16px",
                          }}
                        >
                          {option.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                ) : (
                  <ListItemButton
                    component={LocalizedLink}
                    to={option.route}
                    onClick={() => toggleDrawer(false)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(196, 106, 45, 0.1)",
                        color: "#9E4E1F",
                        borderRadius: "8px",
                      },
                      transition: "all 0.3s ease-in-out", // Smooth transition
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontFamily: "Roboto Bold, sans-serif",
                            padding: "1px 0px",
                          }}
                        >
                          {option.name}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                )}
                {option.collapse && (
                  <>
                    <List component="div" disablePadding>
                      {option.collapse.map((suboption) => (
                        <ListItemButton
                          button
                          key={suboption.name}
                          component={LocalizedLink}
                          to={suboption.route}
                          onClick={() => toggleDrawer(false)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "rgba(196, 106, 45, 0.1)",
                              color: "#9E4E1F",
                              borderRadius: "8px",
                            },
                            transition: "all 0.3s ease-in-out", // Smooth transition
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontFamily: "Roboto Regular, sans-serif",
                                  padding: "2px 9px",
                                }}
                              >
                                {suboption.name}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                    <Divider sx={{ my: 1 }} />
                  </>
                )}
              </React.Fragment>
            ))}
          </List>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
